import { normalizeDateForCompare } from './tanstackGridColumns';

export const HIGHLIGHT_PALETTE = [
  { id: 'yellow', label: '노랑', token: 'yellow' },
  { id: 'green', label: '초록', token: 'green' },
  { id: 'blue', label: '파랑', token: 'blue' },
  { id: 'orange', label: '주황', token: 'orange' },
  { id: 'pink', label: '분홍', token: 'pink' },
  { id: 'purple', label: '보라', token: 'purple' },
  { id: 'red', label: '빨강', token: 'red' },
];

export const OP_LABEL = {
  gte: '≥',
  lte: '≤',
  gt: '>',
  lt: '<',
  eq: '=',
  between: '구간',
  contains: '포함',
  notContains: '미포함',
  startsWith: '시작',
  empty: '빈 값',
  notEmpty: '값 있음',
};

export function inferColumnKind(meta) {
  const t = meta?.type;
  if (t === 'number') return 'number';
  if (t === 'date') return 'date';
  if (t === 'combo' || t === 'checkbox') return 'code';
  return 'text';
}

export function operatorsForKind(kind) {
  if (kind === 'number' || kind === 'date') {
    return ['gte', 'lte', 'gt', 'lt', 'eq', 'between', 'empty', 'notEmpty'];
  }
  return ['contains', 'notContains', 'startsWith', 'eq', 'empty', 'notEmpty'];
}

export function resolveHighlightBg(colorId, theme) {
  if (!colorId || !theme?.colors) return null;
  const raw = theme.colors[colorId] || theme.colors.status?.[colorId];
  if (!raw) return null;
  return `color-mix(in srgb, ${raw} 38%, ${theme.colors.white})`;
}

function toNumber(value) {
  if (vIsEmpty(value)) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const n = Number(String(value).replace(/[,원\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function vIsEmpty(value) {
  return value == null || value === '';
}

function asText(value) {
  if (vIsEmpty(value)) return '';
  return String(value);
}

/**
 * 조건이 행 값과 맞는지
 * @param {unknown} cellValue
 * @param {{ op: string, value?: string, value2?: string }} rule
 * @param {'number'|'date'|'code'|'text'} kind
 */
export function matchCondition(cellValue, rule, kind) {
  if (!rule?.op) return true;
  const op = rule.op;
  if (op === 'empty') return vIsEmpty(cellValue);
  if (op === 'notEmpty') return !vIsEmpty(cellValue);

  if (kind === 'number') {
    const n = toNumber(cellValue);
    const a = toNumber(rule.value);
    const b = toNumber(rule.value2);
    if (n == null) return false;
    if (op === 'gte') return a != null && n >= a;
    if (op === 'lte') return a != null && n <= a;
    if (op === 'gt') return a != null && n > a;
    if (op === 'lt') return a != null && n < a;
    if (op === 'eq') return a != null && n === a;
    if (op === 'between') {
      if (a == null || b == null) return false;
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      return n >= lo && n <= hi;
    }
    return true;
  }

  if (kind === 'date') {
    const n = normalizeDateForCompare(cellValue);
    const a = rule.value ? normalizeDateForCompare(rule.value) : '';
    const b = rule.value2 ? normalizeDateForCompare(rule.value2) : '';
    if (!n) return false;
    if (op === 'gte') return a && n >= a;
    if (op === 'lte') return a && n <= a;
    if (op === 'gt') return a && n > a;
    if (op === 'lt') return a && n < a;
    if (op === 'eq') return a && n === a;
    if (op === 'between') {
      if (!a || !b) return false;
      const lo = a < b ? a : b;
      const hi = a < b ? b : a;
      return n >= lo && n <= hi;
    }
    return true;
  }

  const t = asText(cellValue).toLowerCase();
  const a = asText(rule.value).toLowerCase();
  if (op === 'contains') return a ? t.includes(a) : true;
  if (op === 'notContains') return a ? !t.includes(a) : true;
  if (op === 'startsWith') return a ? t.startsWith(a) : true;
  if (op === 'eq') return t === a;
  return true;
}

export function matchRuleOnRow(rowOriginal, rule, kind) {
  if (!rule?.columnId) return false;
  return matchCondition(rowOriginal?.[rule.columnId], rule, kind || rule.kind || 'text');
}

/** 숨김(필터) 규칙 AND — 모두 통과해야 행이 남음 */
export function rowMatchesAllHideRules(rowOriginal, rules) {
  const hide = (rules || []).filter((r) => r.hide);
  if (!hide.length) return true;
  return hide.every((r) => matchRuleOnRow(rowOriginal, r, r.kind || 'text'));
}

/**
 * 셀/행 강조 배경. 셀 색이 행 색보다 우선. 선택 행은 호출 측에서 처리.
 */
export function resolveFormatColor(rules, columnId, rowOriginal, theme) {
  let rowBg = null;
  let cellBg = null;
  for (const rule of rules || []) {
    if (!rule.cellColor && !rule.rowColor) continue;
    if (!matchRuleOnRow(rowOriginal, rule, rule.kind || 'text')) continue;
    if (rule.rowColor) rowBg = resolveHighlightBg(rule.rowColor, theme);
    if (rule.cellColor && rule.columnId === columnId) {
      cellBg = resolveHighlightBg(rule.cellColor, theme);
    }
  }
  return cellBg || rowBg || null;
}

export function formatRuleChip(rule) {
  const op = OP_LABEL[rule.op] || rule.op;
  const header = rule.header || rule.columnId;
  let cond = '';
  if (rule.op === 'empty' || rule.op === 'notEmpty') {
    cond = `${header} ${op}`;
  } else if (rule.op === 'between') {
    cond = `${header} ${formatDisp(rule.value)} ~ ${formatDisp(rule.value2)}`;
  } else {
    cond = `${header} ${op} ${formatDisp(rule.value)}`;
  }
  const acts = [];
  if (rule.hide) acts.push('필터');
  if (rule.cellColor) acts.push('셀색');
  if (rule.rowColor) acts.push('행색');
  const extra = [];
  if (acts.length) extra.push(acts.join('+'));
  if (rule.persist) extra.push('유지');
  return extra.length ? `${cond} · ${extra.join(' · ')}` : cond;
}

function formatDisp(v) {
  if (v == null || v === '') return '';
  const n = toNumber(v);
  if (n != null && String(v).replace(/[,원\s]/g, '') !== '' && !String(v).includes('-')) {
    return n.toLocaleString('ko-KR');
  }
  return String(v);
}

export function createRuleId(columnId) {
  return `cond:${columnId}`;
}

export function normalizeRule(input, columnMeta) {
  const kind = input.kind || inferColumnKind(columnMeta);
  return {
    id: input.id || createRuleId(input.columnId),
    columnId: input.columnId,
    header: input.header || input.columnId,
    kind,
    op: input.op,
    value: input.value ?? '',
    value2: input.value2 ?? '',
    hide: Boolean(input.hide),
    cellColor: input.cellColor || null,
    rowColor: input.rowColor || null,
    persist: Boolean(input.persist),
  };
}

export function formatListFilterChip(header, selectedCount) {
  return `${header} · ${selectedCount}개 선택`;
}
